import { useContext } from 'react';
import ivclass from '@invideoio/web-shared/Modules/IVClass';

import IVButton from '@invideoio/uniformd/components/Button';
import ImageComponent from '@components/common/ImageComponent/ImageComponent';
import { AUTH_TYPE, HOMEPAGE } from '@constants/homepage';
import {
  AmplitudeEvents,
  sendAmplitudeAnalyticEvent,
  sendRudderStackAnalyticEvent,
} from '@utils/analytics';
import { RedirectContext } from '@store/store';
import { SubFooterProps } from 'src/types';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import useTranslation from 'next-translate/useTranslation';
import { ClickCtaUxSource, ClickCtaType } from 'src/types/analytics';

const getImageSize = (platfrom: PlatformType) => {
  switch (platfrom) {
    case PlatformType.Mobile:
      return { width: 36, height: 37 };
    default:
      return { width: 56, height: 54 };
  }
};

const SubFooter = (props: SubFooterProps) => {
  const {
    detail = null,
    CTAUrl = HOMEPAGE.superbVideo.CTAUrl,
    platform,
  } = props;

  const { t } = useTranslation('common');

  const { logo } = HOMEPAGE.superbVideo;
  const { handleClick } = useContext(RedirectContext);
  const onClick = () => {
    sendAmplitudeAnalyticEvent(AmplitudeEvents.clickSignUp, {
      btnPosition: 'Bottom',
    });
    sendRudderStackAnalyticEvent({
      event_name: 'click_cta',
      event_properties: {
        ux_source: ClickCtaUxSource.Bottom,
        type: ClickCtaType.ClickSignup,
        feature_utm: 'na',
      },
    });
    handleClick(CTAUrl, AUTH_TYPE.SIGNUP);
  };

  return (
    <section
      className={ivclass(
        'iv-bg-grey-100',
        'xl:iv-py-100 iv-pt-30 iv-pb-35',
        'iv-text-center',
      )}
    >
      <div
        className={ivclass(
          'iv-inline-block',
          'iv-my-35 sm:iv-my-[42px]',
          'iv-relative',
          'iv-h-[54px] sm:iv-h-[37px]',
          'iv-w-[56px] sm:iv-w-[36px]',
        )}
      >
        <ImageComponent
          elementype="native"
          {...getImageSize(platform)}
          src={logo.url}
          alt={logo.alt}
          loading="lazy"
        />
      </div>
      <h2
        className={ivclass(
          'xl:iv-pt-40 iv-pt-[24px] sm:iv-pt-7',
          'iv-text-center',
          'iv-text-grey-0',
          'xl:iv-text-heading-4xl iv-text-heading-3xl sm:iv-text-heading-s',
          'xl:iv-m-heading-4xl iv-m-heading-3xl sm:iv-m-heading-s',
          'iv-font-extrabold',
          'iv-w-full',
        )}
      >
        <span className={ivclass()}>{t('subfooter.plain')}</span>
        <span
          className={ivclass(
            'iv-block',
            'iv-text-center',
            'iv-title-gradient',
            'iv-font-extrabold',
            'iv-leading-tight',
          )}
        >
          {t('subfooter.highlighted')}
        </span>
      </h2>
      {detail && (
        <div
          className={ivclass(
            'iv-px-20',
            'iv-pt-60 sm:iv-pt-40',
            'iv-text-center',
            'iv-text-grey-0',
            'xl:iv-text-heading-2xs lg:iv-text-heading-2xs md:iv-text-heading-3xs sm:iv-text-body-2xl',
            'xl:iv-m-heading-2xs lg:iv-m-heading-2xs md:iv-m-heading-3xs sm:iv-m-body-2xl',
            'iv-font-semibold',
          )}
        >
          <div>{detail.highlightedText}</div>
          <span
            className={ivclass('iv-block sm:iv-inline', 'iv-text-white-50')}
          >
            {detail.plainText1}
          </span>
          <span
            className={ivclass('iv-block sm:iv-inline', 'iv-text-white-50')}
          >
            {detail.plainText2}
          </span>
        </div>
      )}
      <IVButton
        className={ivclass(
          'iv-bg-blue hover:iv-bg-blue-strong iv-transition',
          'iv-flex',
          'iv-justify-center',
          'iv-items-center',
          'iv-cursor-pointer',
          'iv-rounded-full',
          'xl:iv-mt-80 iv-mt-75 sm:iv-mt-60',
          'xl:iv-mb-50 iv-mb-40',
          'iv-mx-auto',
          'xl:iv-px-40 iv-px-30',
          'xl:iv-h-65 iv-h-52',
          'xl:iv-text-body-3xl iv-text-body-2xl',
          'iv-text-grey-0 iv-font-bold',
        )}
        onClick={onClick}
      >
        {t('subfooter.CtaText')}
      </IVButton>
    </section>
  );
};

export default SubFooter;
